/**
 * The current user's effective rights on the selected connection (Task 5,
 * ERP Users & Access Rights epic). Mirrors useConfigMenus.ts's react-query
 * shape, but sources the connection id from context rather than a param —
 * every consumer of this hook already renders inside a connection.
 */
import { useQuery } from 'react-query'

import { useConnection } from '@/app/contexts/ConnectionContext'
import { runtimeApi } from '@/app/services/runtimeApi'
import type { MyPermissions } from './permissionHelpers'

export type { MyPermissions } from './permissionHelpers'

export function useMyPermissions(): { perms?: MyPermissions; loading: boolean } {
  const { selectedConnection } = useConnection()
  const connectionId = selectedConnection?.id

  const { data, isLoading, isIdle } = useQuery<MyPermissions>(
    ['my-permissions', connectionId],
    () => runtimeApi.myPermissions(connectionId as string),
    { enabled: !!connectionId, staleTime: 30_000 },
  )

  // `enabled: false` (no connectionId at all — none selected, or the
  // selected one was just cleared/deleted) makes react-query v3 report
  // `isLoading: false` with `isIdle: true`, not "still loading". A caller
  // that reads `loading` to decide whether it's SAFE to act on a denial
  // (ProtectedDesignerRoute) must not treat idle as "resolved and denied" —
  // it may simply mean there's no connection to resolve permissions for
  // yet. Folding isIdle in here keeps every caller's check simple; the
  // caller still needs its OWN "is there a connection at all" check for the
  // genuinely-no-connection case (see ConnectionContext's `hydrated` +
  // ProtectedDesignerRoute) — this hook only ever answers for the
  // connection it's given.
  return { perms: data, loading: isLoading || isIdle }
}
